<template>
    <div>
        <div class="filters-bar-container">
            <v-container class="py-0">
                <FiltersBar ref="filtersBar" searchClass="pb-3" wrapperClass="pb-0" alwaysMinified :minWidth="6" :search="search" @update:search="search = $event"></FiltersBar>
            </v-container>
        </div>
        <v-expansion-panels
            class="expansion-panels"
            v-model="panel"
            multiple
            focusable
            accordion
        >
            <v-expansion-panel :disabled="selected.length === 0">
                <v-expansion-panel-header>
                    <v-row no-gutters>
                        <v-col>
                            {{ $t('tracks.message.selected') }}
                        </v-col>
                        <v-col cols="auto" class="mr-3">
                            <v-badge :content="selected.length" :value="selected.length" color="secondary" inline />
                        </v-col>
                    </v-row>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <TreeView v-model="selected" :items="selected" />
                </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
                <v-expansion-panel-header>
                    <v-row no-gutters>
                        <v-col>
                            {{ $t('tracks.message.employees') }}
                        </v-col>
                        <v-col cols="auto" class="mr-4">
                            <small>{{ $t('tracks.message.displaying') }}: <strong>{{ employeesTree.length }}/{{ totalEmployeesAmount }}</strong></small>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon
                                        color="secondary"
                                        v-bind="attrs"
                                        v-on="on"
                                        size="medium"
                                        class="ml-1"
                                    >
                                    {{ ICONS.INFORMATION }}
                                    </v-icon>
                                </template>
                                <span>{{ $t('tracks.message.displaying_tooltip') }}</span>
                            </v-tooltip>
                        </v-col>
                    </v-row>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <TreeView v-model="selected" :items="employeesTree" />
                    <SkeletonLoaderTree v-if="isLoading" />
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
    </div>
</template>

<script>
import AutocompleteFilter from '@/models/filters/autocompleteFilter';
import debounce from 'lodash.debounce';
import debouncedEmployees from '@/mixins/debounced/debouncedEmployees';
import debouncedEmployeesGroups from '@/mixins/debounced/debouncedEmployeesGroups';
import debouncedCustomers from '@/mixins/debounced/debouncedCustomers';
import debouncedSites from '@/mixins/debounced/debouncedSites';
import debouncedLocations from '@/mixins/debounced/debouncedLocations';
import debouncedCompanies from '@/mixins/debounced/debouncedCompanies';
import debouncedPositions from '@/mixins/debounced/debouncedPositions';
import { ICONS } from '@/constants/icons';
import filtering from '@/mixins/filtering';
import FiltersBar from '@/components/widgets/tables/FiltersBar.vue';
import { FILTERS, FILTERS_SEARCHING } from '@/constants/filtersModelNames';
import { mapState } from 'vuex';
import TreeView from '@/components/treeView/TreeView.vue';
import SkeletonLoaderTree from './SkeletonLoaderTree.vue';

export default {
    name: 'LazyEmployeesTree',
    mixins: [
        filtering,
        debouncedEmployees,
        debouncedEmployeesGroups,
        debouncedCustomers,
        debouncedSites,
        debouncedLocations,
        debouncedCompanies,
        debouncedPositions
    ],
    components: { FiltersBar, TreeView, SkeletonLoaderTree },
    props: {
        employeesTree: Array,
        initiallySelected: Array,
        isLoading: Boolean
    },
    data: () => ({
        search: null,
        selected: [],
        ICONS,
        debouncedFetchEmployees: null,
        pagination: {
            page: 1
        },
        initialed: false,
        panel: [1]
    }),
    computed: {
        ...mapState('filters', [
            'filters',
            'filtersSearching'
        ]),
        ...mapState('manageTrack', [
            'trackData'
        ]),
        ...mapState('employees', [
            'totalEmployeesAmount'
        ]),
        filtersConfig () {
            return [
                new AutocompleteFilter({
                    model: FILTERS.EMPLOYEE_GROUP_ID,
                    label: this.$t('navigation.employees_groups'),
                    loading: true,
                    multiple: true,
                    search: FILTERS_SEARCHING.EMPLOYEES_GROUPS_NAME,
                    lazy: this.getEmployeesGroupsLazyLoading,
                    searchCallback: this.debouncedGetEmployeesGroups,
                    items: {
                        obj: 'getters',
                        module: 'employeesGroups',
                        prop: 'filterableEmployeesGroups'
                    }
                }),
                new AutocompleteFilter({
                    model: FILTERS.COMPANY_ID,
                    label: this.$t('navigation.companies'),
                    loading: true,
                    multiple: true,
                    search: FILTERS_SEARCHING.COMPANIES,
                    lazy: this.getCompaniesLazyLoading,
                    searchCallback: this.debouncedGetCompanies,
                    items: {
                        obj: 'getters',
                        module: 'companies',
                        prop: 'filterableCompanies'
                    }
                }),
                new AutocompleteFilter({
                    model: FILTERS.POSITION_ID,
                    label: this.$t('navigation.position'),
                    loading: true,
                    multiple: true,
                    search: FILTERS_SEARCHING.POSITIONS,
                    lazy: this.getPositionsLazyLoading,
                    searchCallback: this.debouncedGetPositions,
                    items: {
                        obj: 'getters',
                        module: 'positions',
                        prop: 'humanPositions'
                    }
                })
            ];
        }
    },
    watch: {
        selected: {
            handler (val, oldVal) {
                if (val.length === 0) {
                    this.panel = this.panel.filter(panel => panel !== 0);
                }

                if (JSON.stringify(val) === JSON.stringify(oldVal) && val.length !== 0) return;
                this.$store.dispatch('cyclicRevisions/setSelectedTracksEmployees', val);
            }
        },
        search: {
            handler (val, oldVal) {
                if ((val && val.length >= 2) || (oldVal && val === '')) {
                    this.debouncedFetchEmployees();
                }
            }
        },
        initiallySelected: {
            handler (val) {
                if (this.initialed) return;

                this.selected = val;
                this.initialed = true;
            }
        }
    },
    methods: {
        selectEmployee (employee) {
            if (this.selected.includes(employee)) {
                this.selected = this.selected.filter(i => i.id !== employee.id);
            } else {
                this.selected = [...this.selected, employee];
            }
        },
        removeSelected (item) {
            this.selected = this.selected.filter(i => i.id !== item.id);
        },
        generateFilters () {
            const params = {};

            this.filtersConfig.forEach(filter => {
                const f = filter.model;
                if ([FILTERS.SITE_ID, FILTERS.CUSTOMER_ID].includes(f)) {
                    return;
                }
                if (this.filters[f] && this.filters[f].length > 0) {
                    params[`filter[${f}]`] = this.filters[f].join(',');
                }
            });

            return params;
        },
        fetchEmployees () {
            const params = {
                'filter[is_item]': false,
                'page[number]': this.pagination.page,
                ...this.generateFilters()
            };

            if (this.search) {
                params['filter[search]'] = this.search;
            }
            this.$emit('get-employees', params);
        },
        setFiltersWatcher (callback) {
            this.localConfig.forEach(filter => {
                const el = filter.model;
                this.$watch(`${this.filterPropertyName}.${el}`, function (newVal) {
                    if (newVal) {
                        if (this.pagination) {
                            this.pagination.page = 1;
                        }
                        callback();
                        if (filter.hasProperty('referralCallback')) {
                            filter.referralCallback(newVal);
                        }
                    } else if (newVal === null) {
                        callback();
                    }
                });
            });
        }
    },
    mounted () {
        this.$store.commit('filters/SET_CONFIG', this.filtersConfig);
        this.$store.dispatch('employeesGroups/getEmployeesGroups')
            .then(() => {
                this.filtersConfig.find(el => el.model === FILTERS.EMPLOYEE_GROUP_ID).loading = false;
                this.$store.commit('filters/SET_CONFIG', this.filtersConfig);
            });
        this.debouncedFetchEmployees = debounce(this.fetchEmployees, 1000);
        this.setFiltersWatcher(this.debouncedFetchEmployees);
    },

    beforeUnmount () {
        this.$store.dispatch('filters/clearFiltersParams');
        this.search = null;
        this.debouncedFetchEmployees = null;
    }
};
</script>

<style scoped lang="scss">
    .filters-bar-container {
        position: sticky;
        top: 0;
        z-index: 1;
        background-color: #fff;
        border-bottom: 1px solid #e0e0e0;
    }
    .expansion-panels {
        z-index: 0;

        ::v-deep {
            .v-expansion-panel-content__wrap {
                padding: 0;
            }

            .v-expansion-panel-header {
                padding: 12px;
            }
        }
    }
    ::v-deep {
         .v-treeview-node--disabled {
            display: none;
         }
    }
</style>
